<script>
import {defineComponent} from 'vue'
import Vue from 'vue'
import api from "@/api/api"
import TicketLayouts from "@/components/Tickets/layouts/TicketLayouts.vue"
import TicketBoardCardTemplate from "@/components/Tickets/widgets/TicketBoardCardTemplate.vue"
import {mapActions, mapGetters} from "vuex"
import {connectToWs, subscribe, onMessage, sendMessage, closeWs} from "@/services/websocket"
import {BIconSendFill} from 'bootstrap-vue'
import {statusPriority} from "@/constants/ticket-status"

// Utilities
import {formatFileSize, isImage, getFileIcon, downloadFile} from "@/common/fileUtils"
import {getInitials} from "@/common/stringUtils"
import {scrollToElement, findTicketCard} from "@/common/scrollUtils"
import {mapTicketFromAPI, getActionOptionsForTicket, buildTicketPayload} from "@/common/ticketMapper"
import {mergeTicketsData} from "@/common/kanbanHelpers"
import {PRIORITY_OPTIONS, TICKETS_PER_PAGE, TICKET_ACTIONS_PER_PAGE} from "@/constants/ticketConstants"

export default defineComponent({
  name: "TicketBoard",
  components: {TicketLayouts, BIconSendFill},

  data() {
    return {
      statuses: [],
      kanbanData: [],
      history: [],
      isLoading: false,
      statusPagination: {},
      loadingStatuses: {},
      scrollDebounceTimers: {},
      payload: {
        id: null,
        title: '',
        description: '',
        priority: '',
        action_id: null,
        category: {
          id: null,
          name: ''
        },
        type: {
          id: null,
          name: ''
        },
      },
      isSidebarLoading: false,
      isHistoryTab: false,
      sidebarTicket: null,
      priorityOptions: PRIORITY_OPTIONS,
      cardSettings: {
        contentField: "Summary",
        headerField: "Id",
        template: () => {
          return {
            template: Vue.extend({
              ...TicketBoardCardTemplate,
              methods: {
                ...TicketBoardCardTemplate.methods,
                openHistory() {
                  this.$root.$emit('ticket-open-history', this.data)
                },
                openChat() {
                  this.$root.$emit('ticket-open-chat', this.data)
                },
              }
            })
          }
        }
      },
      messages: [],
      newMessageText: '',
      isSendingMessage: false,
      attachedFiles: [],
      token: localStorage.getItem('token'),
    }
  },

  computed: {
    ...mapGetters('moduleTickets', ['ticketActions', 'categoryOptions', 'typeOptions']),
    ...mapGetters(['getUserId', 'getUserName', 'getUserEmail']),

    userId() {
      return this.getUserId
    },
    userName() {
      return this.getUserName
    },
    userEmail() {
      return this.getUserEmail
    },
    sortedStatuses() {
      const statuses = Array.isArray(this.statuses) ? this.statuses : []
      return statuses.slice().sort((a, b) => {
        const aPriority = statusPriority[a.slug] ?? Number.MAX_SAFE_INTEGER
        const bPriority = statusPriority[b.slug] ?? Number.MAX_SAFE_INTEGER
        return aPriority - bPriority
      })
    },
    actionOptions() {
      return getActionOptionsForTicket(this.payload.action_id, this.ticketActions)
    },
    reversedMessages() {
      return this.messages.slice().reverse().map(msg => ({
        ...msg,
        initials: this.getInitials(msg.user && msg.user.username || 'U'),
        isMine: this.isMyMessage(msg)
      }))
    }
  },
  methods: {
    getInitials,
    formatFileSize,
    ...mapActions('moduleTickets', ['getTicketActions', 'getTicketCategories', 'getTicketTypes']),
    async onSelectTicketPriority() {
      this.isSidebarLoading = true
      try {
        const {id, priority} = this.payload
        const res = await api.tickets.updateTicket(id, {priority})
        if (res.status === 204) {
          this.kanbanData = this.kanbanData.map(ticket =>
              ticket.Id === id ? {...ticket, Priority: priority} : ticket
          )

        }
        this.isSidebarLoading = false
        this.$nextTick(() => {
          this.scrollToTicket(id)
          this.scrollToBottom()
        })
      } catch (e) {
        this.$toast.error('Ошибка при обновлении тикета')
        this.$nextTick(() => this.scrollToBottom())
      } finally {
        this.isSidebarLoading = false
      }
    },
    async onSelectTicketCategory() {
      this.isSidebarLoading = true
      try {
        const {id, category} = this.payload
        const res = await api.tickets.updateTicket(id, {category_id: category.id})
        if (res.status === 204) {
          const categoryName = this.categoryOptions.find(cat => cat.value === category.id)?.text
          this.kanbanData = this.kanbanData.map(ticket =>
              ticket.Id === id ? {...ticket, Category: categoryName, CategoryId: category.id} : ticket
          )
        }
        this.isSidebarLoading = false
        this.$nextTick(() => {
          this.scrollToTicket(id)
          this.scrollToBottom()
        })
      } catch (e) {
        this.$toast.error('Ошибка при обновлении тикета')
        this.$nextTick(() => this.scrollToBottom())
      } finally {
        this.isSidebarLoading = false
      }
    },
    async onSelectTicketType() {
      this.isSidebarLoading = true
      try {
        const {id, type} = this.payload
        const res = await api.tickets.updateTicket(id, {type_id: type.id})
        if (res.status === 204) {
          const typeName = this.typeOptions.find(t => t.value === type.id)?.text
          this.kanbanData = this.kanbanData.map(ticket =>
              ticket.Id === id ? {...ticket, Type: typeName, TypeId: type.id} : ticket
          )
        }
        this.isSidebarLoading = false
        this.$nextTick(() => {
          this.scrollToTicket(id)
          this.scrollToBottom()
        })
      } catch (e) {
        this.$toast.error('Ошибка при обновлении тикета')
        this.$nextTick(() => this.scrollToBottom())
      } finally {
        this.isSidebarLoading = false
      }
    },
    async onSelectTicketAction() {
      this.isSidebarLoading = true
      try {
        const {id, action_id} = this.payload
        await api.tickets.updateActionFromTicket(id, action_id, {action_id})
        await this.getListTickets()
        // После смены действия (например, эскалации на другую линию) текущее действие тикета
        // определяет сервер, поэтому перечитываем тикет — иначе список доступных действий
        // остаётся от прежней линии до переоткрытия деталей
        await this.refreshSidebarTicket(id)
        this.isSidebarLoading = false
        this.$nextTick(() => {
          this.scrollToTicket(id)
          this.scrollToBottom()
        })
      } catch (e) {
        this.$toast.error('Ошибка при обновлении тикета')
        this.$nextTick(() => this.scrollToBottom())
      } finally {
        this.isSidebarLoading = false
      }
    },
    handleMessage(data) {
      switch (data.type || data.event) {
        case 'ticket.message.sent':
          this.handleNewMessage(data);
          break;
        case 'ticket_updated':
          this.getListTickets();
          break;
        case 'subscription_confirmed':
          console.log('✅ Subscribed to:', data.channel);
          break;
        default:
          console.log('⚠️ Unknown message type:', data);
      }
    },

    handleNewMessage(data) {
      if (this.isHistoryTab || this.sidebarTicket?.Id !== data.data.ticket_message.ticket_id) {
        return;
      }

      const message = data.data?.ticket_message || data.data;
      const exists = this.messages.some(m => m.id === message.id);

      if (!exists) {
        this.messages.unshift(message);
        this.$nextTick(() => this.scrollToBottom());
      }
    },
    subscribeToTicketChat(ticketId) {
      subscribe(`ticket.${ticketId}.messages`, this.token);
    },

    async getListTickets() {
      this.isLoading = true;
      try {
        await this.getStatuses()

        const statusLoadPromises = this.statuses.map(status =>
            this.loadTicketsForStatus(status.id, 1, true)
        )

        await Promise.all(statusLoadPromises)
        this.isLoading = false;
      } catch (e) {
        this.isLoading = false
      }
    },

    async loadTicketsForStatus(statusId, page = 1, isInitial = false) {
      try {
        if (!isInitial) {
          this.$set(this.loadingStatuses, statusId, true)
          this.showLoadingIndicator(statusId)
        }

        const res = await api.tickets.getListTickets({
          status: statusId,
          page,
          per_page: TICKETS_PER_PAGE
        })

        const tickets = res.data?.data?.map(ticket => mapTicketFromAPI(ticket, this.ticketActions)) || []
        const currentStatusSlug = this.statuses.find(s => s.id === statusId)?.slug

        this.kanbanData = mergeTicketsData(this.kanbanData, tickets, currentStatusSlug, page === 1)

        this.$set(this.statusPagination, statusId, {
          current_page: res.data?.meta?.current_page || page,
          last_page: res.data?.meta?.last_page || 1,
          per_page: res.data?.meta?.per_page || 20,
          total: res.data?.meta?.total || 0
        })

        if (!isInitial) {
          this.$set(this.loadingStatuses, statusId, false)
          this.hideLoadingIndicator(statusId)
        }

      } catch (e) {
        if (!isInitial) {
          this.$set(this.loadingStatuses, statusId, false)
          this.hideLoadingIndicator(statusId)
        }
      }
    },

    async getStatuses() {
      try {
        const res = await api.tickets.getListActionStatuses()
        this.statuses = res.data?.data
      } catch (e) {
        console.log(e)
      }
    },

    async updateTicket() {
      this.isSidebarLoading = true
      try {
        const {id, ...updateData} = this.payload
        await api.tickets.updateTicket(id, updateData)
        this.$toast.success('Тикет обновлен')
        this.$root.$emit('bv::hide::modal', 'ticket-modal')
        await this.getListTickets()
        this.isSidebarLoading = false
      } catch (e) {
        this.$toast.error('Ошибка при обновлении тикета')
      }
    },

    async getTicketHistory(ticketId) {
      try {
        this.isSidebarLoading = true
        const res = await api.tickets.getTicketHistory(ticketId)
        this.history = res.data.data
      } catch (e) {
        this.$toast.error('Ошибка при получении истории тикета')
      } finally {
        this.isSidebarLoading = false
      }
    },

    handleSidebarClose() {
      this.sidebarTicket = null
      this.isHistoryTab = false
      document.body.style.overflow = 'auto'
    },
    async openSidebar(ticketData, mode = 'history') {
      this.sidebarTicket = ticketData
      this.isHistoryTab = mode === 'history'
      if (ticketData) {
        this.payload = {
          id: ticketData.Id,
          title: ticketData.Title,
          description: ticketData.Summary,
          priority: ticketData.Priority,
          action_id: ticketData.ActionId,
          category: {
            id: ticketData.CategoryId,
            name: ticketData.Category
          },
          type: {
            id: ticketData.TypeId,
            name: ticketData.Type
          },
        }
      }

      this.$root.$emit('bv::toggle::collapse', 'sidebar-right')

      if (!ticketData?.Id) return

      if (mode === 'history') {
        await this.getTicketHistory(ticketData.Id)
      } else if (mode === 'chat') {
        this.subscribeToTicketChat(ticketData.Id)
        await this.getTicketMessages(ticketData.Id)
        this.$nextTick(() => this.scrollToBottom())
        const markReadMessageStatus = (await api.tickets.markMessageAsRead(ticketData.Id)).status
        if (markReadMessageStatus === 204) {
          ticketData.UnreadMessageCount = 0
        }
      }
    },

    async refreshSidebarTicket(ticketId) {
      if (!ticketId) return
      try {
        const res = await api.tickets.getTicket(ticketId)
        const ticket = res.data?.data || res.data
        // без action пересобирать нечего — оставляем текущие данные, чтобы не обнулить действие
        if (!ticket?.id || !ticket?.action?.id) return

        const mappedTicket = mapTicketFromAPI(ticket, this.ticketActions)
        this.kanbanData = this.kanbanData.map(item =>
            item.Id === mappedTicket.Id ? {...item, ...mappedTicket} : item
        )

        if (this.sidebarTicket?.Id === mappedTicket.Id) {
          this.sidebarTicket = mappedTicket
          this.payload = buildTicketPayload(mappedTicket)
        }
      } catch (e) {
        console.log(e)
      }
    },

    async getTicketMessages(ticketId) {
      try {
        this.isSidebarLoading = true
        this.attachedFiles = []
        const res = await api.tickets.getListOfMessages(ticketId)
        this.messages = res.data?.data || []
        this.$nextTick(() => this.scrollToBottom())
      } catch (e) {
        this.$toast.error('Ошибка при получении сообщений')
      } finally {
        this.isSidebarLoading = false
      }
    },
    async handleSendMessage() {
      if (!this.newMessageText.trim() && !this.attachedFiles.length) {
        this.$toast.warning('Введите сообщение или прикрепите файл')
        return
      }

      if (this.isSendingMessage) return
      this.isSendingMessage = true

      try {
        const formData = new FormData()
        formData.append('message', this.newMessageText)
        this.attachedFiles.forEach((file) => {
          formData.append('attachments[]', file.raw)
        })

        const res = await api.tickets.sendMessage(this.sidebarTicket.Id, formData)

        this.messages.unshift(res.data.data)
        sendMessage({
          channel: `ticket.${this.sidebarTicket.Id}.messages`,
          event: 'ticket.message.sent',
          data: {
            ticket_id: res.data.data.ticket_id,
            message: res.data.data.message,
            id: res.data.data.id,
            user_id: res.data.data.user_id,
            user: res.data.data.user,
            attachments: res.data.data.attachments,
            created_at: res.data.data.created_at,
          }
        })
        this.newMessageText = ''
        this.attachedFiles = []
        this.$nextTick(() => this.scrollToBottom())
        this.$toast.success('Сообщение отправлено')

      } catch (e) {
        this.$toast.error('Ошибка отправки сообщения')
      } finally {
        this.isSendingMessage = false
      }
    },
    scrollToBottom() {
      const chatMessages = this.$refs.chatMessages
      if (chatMessages) {
        chatMessages.scrollTop = chatMessages.scrollHeight
      }
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files)
      files.forEach(file => {
        const fileObj = {
          raw: file,
          name: file.name,
          size: file.size,
          preview: null,
        }
        if (isImage(file.name)) {
          fileObj.preview = URL.createObjectURL(file)
        }
        this.attachedFiles.push(fileObj)
      })
      this.$refs.fileInput.value = ''
    },
    removeAttachedFile(index) {
      const file = this.attachedFiles[index]
      if (file.preview) {
        URL.revokeObjectURL(file.preview)
      }
      this.attachedFiles.splice(index, 1)
    },

    // ===== Scroll Utilities =====
    scrollToTicket(ticketId) {
      setTimeout(() => {
        const kanbanElement = document.getElementById('kanban')
        if (!kanbanElement) return

        const ticketCard = findTicketCard(kanbanElement, ticketId)
        if (ticketCard) {
          scrollToElement(ticketCard)
        }
      }, 300)
    },

    // ===== File & Message Helpers =====
    isMyMessage(msg) {
      return msg.user?.username === this.userName || msg.user?.email === this.userEmail
    },

    isImage,

    getFileIcon,

    downloadFile(url, filename) {
      downloadFile(url, filename, ({title, message, variant}) => {
        this.$bvToast.toast(message, {
          title,
          variant,
          solid: true
        })
      })
    },

    setupScrollHandlers() {
      const kanbanElement = document.getElementById('kanban')
      if (!kanbanElement) {
        setTimeout(() => this.setupScrollHandlers(), 500)
        return
      }

      // Очищаем старые обработчики
      const allScrollables = kanbanElement.querySelectorAll('[data-scroll-handler="true"]')
      allScrollables.forEach(el => {
        if (el._scrollHandler) {
          el.removeEventListener('scroll', el._scrollHandler)
          el._scrollHandler = null
          el.removeAttribute('data-scroll-handler')
        }
      })

      // Находим все колонки по slug статуса
      this.statuses.forEach((status) => {
        // Syncfusion Kanban использует data-key на td.e-content-cells
        const column = kanbanElement.querySelector(`td.e-content-cells[data-key="${status.slug}"]`)

        if (!column) {
          console.warn(`Column not found for status: ${status.slug}`)
          return
        }

        // Скроллируемый элемент внутри колонки
        const scrollableContent = column.querySelector('.e-card-wrapper')

        if (!scrollableContent) {
          console.warn(`Scrollable content not found for status: ${status.slug}`)
          return
        }

        // Проверяем что элемент действительно скроллируется
        const hasOverflow = scrollableContent.scrollHeight > scrollableContent.clientHeight

        // Добавляем индикатор загрузки
        this.addLoadingIndicator(scrollableContent, status.id)

        const scrollHandler = () => {
          this.handleColumnScroll(scrollableContent, status.id, status.slug)
        }

        scrollableContent._scrollHandler = scrollHandler
        scrollableContent._statusId = status.id
        scrollableContent.setAttribute('data-scroll-handler', 'true')
        scrollableContent.addEventListener('scroll', scrollHandler, {passive: true})
      })
    },

    addLoadingIndicator(contentArea, statusId) {
      // Проверяем, нет ли уже индикатора
      if (contentArea.querySelector('.column-loading-indicator')) return

      const loadingDiv = document.createElement('div')
      loadingDiv.className = 'column-loading-indicator'
      loadingDiv.setAttribute('data-status-id', statusId)
      loadingDiv.style.cssText = `
        display: none;
        align-items: center;
        justify-content: center;
        padding: 15px;
        margin: 10px;
        background: #f9fafb;
        border-radius: 8px;
        font-size: 13px;
        color: #667eea;
        gap: 10px;
      `
      loadingDiv.innerHTML = `
        <div class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="sr-only">Загрузка...</span>
        </div>
        <span>Загрузка карточек...</span>
      `
      contentArea.appendChild(loadingDiv)
    },

    showLoadingIndicator(statusId) {
      const kanbanElement = document.getElementById('kanban')
      if (!kanbanElement) return

      const indicator = kanbanElement.querySelector(`.column-loading-indicator[data-status-id="${statusId}"]`)
      if (indicator) {
        indicator.style.display = 'flex'
      }
    },

    hideLoadingIndicator(statusId) {
      const kanbanElement = document.getElementById('kanban')
      if (!kanbanElement) return

      const indicator = kanbanElement.querySelector(`.column-loading-indicator[data-status-id="${statusId}"]`)
      if (indicator) {
        indicator.style.display = 'none'
      }
    },

    handleColumnScroll(element, statusId, statusSlug) {
      // Отменяем предыдущий таймер
      if (this.scrollDebounceTimers[statusId]) {
        clearTimeout(this.scrollDebounceTimers[statusId])
      }

      this.scrollDebounceTimers[statusId] = setTimeout(() => {
        const {scrollTop, scrollHeight, clientHeight} = element

        // Защита от деления на ноль
        if (scrollHeight <= clientHeight) return

        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight
        const isNearBottom = scrollPercentage >= 0.85 // 85% прокручено

        if (isNearBottom) {
          this.loadMoreTicketsForStatus(statusId)
        }
      }, 150) // Уменьшил debounce для лучшей отзывчивости
    },

    async loadMoreTicketsForStatus(statusId) {

      const pagination = this.statusPagination[statusId]

      if (!pagination) {
        return
      }

      if (this.loadingStatuses[statusId]) {
        return
      }

      if (pagination.current_page >= pagination.last_page) {
        return
      }

      const nextPage = pagination.current_page + 1
      await this.loadTicketsForStatus(statusId, nextPage, false)
    },

    getStatusHeaderText(status) {
      const pagination = this.statusPagination[status.id]
      if (pagination) {
        return `${status.name} (${pagination.total})`
      }
      return status.name
    },

    navigateToUser(userId) {
      // Открываем страницу пользователя в новой вкладке
      const route = this.$router.resolve(`/users/${userId}`)
      window.open(route.href, '_blank')
    },

    // Встроенный диалог Syncfusion "Edit Card Details" не используем — тикет редактируется
    // только через сайдбар. Двойной клик по карточке блокируем сразу,
    // dialogOpen страхует остальные пути открытия (клавиатура и т.п.)
    cancelKanbanCardDoubleClick(args) {
      args.cancel = true
    },

    cancelKanbanDialog(args) {
      args.cancel = true
    }
  },

  async mounted() {
    // Действия грузим первыми: по ним строятся списки доступных действий у карточек
    try {
      await this.getTicketActions({page: 1, per_page: TICKET_ACTIONS_PER_PAGE})
    } catch (e) {
      console.log(e)
    }
    this.getListTickets()
    this.getTicketCategories()
    this.getTicketTypes()

    this.$root.$on('ticket-action-updated', this.getListTickets)
    this.$root.$on('ticket-open-history', (ticketData) => this.openSidebar(ticketData, 'history'))
    this.$root.$on('ticket-open-chat', (ticketData) => this.openSidebar(ticketData, 'chat'))

    // Подключаемся к WebSocket
    connectToWs()

    // Устанавливаем обработчик сообщений
    onMessage(this.handleMessage)

    // Подписываемся на каналы
    subscribe(`tickets.${1}`, this.token)
    subscribe(`users.${this.userId}.tickets`, this.token)
    subscribe(`admin-users.${this.userId}.tickets`, this.token)

    this.$nextTick(() => {
      this.setupScrollHandlers()
    })
  },

  beforeDestroy() {
    // Закрываем WebSocket
    closeWs()

    // Отписываемся от событий
    this.$root.$off('ticket-action-updated', this.getListTickets)
    this.$root.$off('ticket-open-history')
    this.$root.$off('ticket-open-chat')
  },
  watch: {
    sidebarTicket: {
      handler(newVal) {
        document.body.style.overflow = newVal?.Id ? 'hidden' : 'auto'
        document.querySelector('.b-sidebar-body').style.overflow = newVal?.Id ? 'hidden' : 'auto'
      },
      deep: true
    },
    isHistoryTab: {
      async handler(newVal) {
        if (newVal === true && this.sidebarTicket?.Id) {
          await this.getTicketHistory(this.sidebarTicket?.Id)
        }
      }
    },
    statuses: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.$nextTick(() => {
            setTimeout(() => this.setupScrollHandlers(), 1000)
          })
        }
      },
      deep: true
    },
    kanbanData: {
      handler(newVal, oldVal) {
        if (newVal?.length > 0) {
          // Переустанавливаем обработчики после загрузки новых данных
          this.$nextTick(() => {
            setTimeout(() => {
              this.setupScrollHandlers()
            }, 500)
          })
        }
      },
      deep: true
    }
  }
})
</script>
<template>
  <TicketLayouts
      :sidebar-loading="isSidebarLoading"
      sidebar-mode="custom"
      :title="`Тикет #${sidebarTicket?.Id}`"
      @sidebar-close="handleSidebarClose"
  >
    <template #sidebar-custom-content="{ item, close }">
      <div class="sidebar-content-wrapper">
        <div class="sidebar-top-section">
          <div>
            <b-form @submit.prevent="updateTicket" class="d-flex justify-content-between align-items-center">
              <b-form-input id="ticket-title" v-model="payload.title" class="mr-3"/>
              <b-button class="w-40" variant="primary" type="submit">
                Изменить
              </b-button>
            </b-form>
            <b-form @submit.prevent="updateTicket" class="d-flex mt-3 justify-content-between align-items-center">
              <b-form-input id="ticket-title" v-model="payload.description" class="mr-3"/>
              <b-button class="w-40" variant="primary" type="submit">
                Изменить
              </b-button>
            </b-form>
            <div class="container mt-2">
              <div class="row g-3">
                <div class="col-6">
                  <b-form-group label="Приоритетность" label-for="ticket-priority">
                    <b-form-select id="ticket-priority" @change="onSelectTicketPriority" v-model="payload.priority"
                                   :options="priorityOptions"/>
                  </b-form-group>
                </div>
                <div class="col-6">
                  <b-form-group label="Действие" label-for="ticket-action">
                    <b-form-select id="ticket-action" @change="onSelectTicketAction" v-model="payload.action_id"
                                   :options="actionOptions"/>
                  </b-form-group>
                </div>
                <div class="col-6">
                  <b-form-group label="Категория" label-for="ticket-category">
                    <b-form-select id="ticket-category" @change="onSelectTicketCategory" v-model="payload.category.id"
                                   :options="categoryOptions"/>
                  </b-form-group>
                </div>
                <div class="col-6">
                  <b-form-group label="Тип" label-for="ticket-type">
                    <b-form-select id="ticket-type" @change="onSelectTicketType" v-model="payload.type.id"
                                   :options="typeOptions"/>
                  </b-form-group>
                </div>
              </div>
            </div>
          </div>
          <div class="tabs w-100 d-flex justify-content-between align-items-center mt-3">
            <b-button @click="isHistoryTab = false" class="w-100 border-none rounded-0"
                      :class="{'active': !isHistoryTab}">
              Chat
            </b-button>
            <b-button @click="isHistoryTab = true" class="w-100 border-none rounded-0" :class="{'active': isHistoryTab}">
              History
            </b-button>
          </div>
        </div>

        <div v-if="isHistoryTab" class="sidebar-scroll-section">
          <div class="history-list mt-3" v-if="history.length">
            <div class="history-item" v-for="(item, index) in history" :key="index">
              <div class="history-icon">
                <BIcon icon="arrow-right-circle"/>
              </div>
              <div class="history-content">
                <div class="history-action">Действие <span>{{ item.action?.title || '—' }}</span></div>
                <div class="history-action">Статус <span>{{ item.action?.status?.name || '—' }}</span></div>
                <div class="history-meta">
                  <span class="history-user">{{ item.action?.group?.group_name || '—' }} | {{ item.created_by?.username || 'Unknown' }}</span>
                  <span class="history-date">{{ new Date(item.created_at).toLocaleString('ru-RU') }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="mt-3">
            <p class="text-center">
              История пуста
            </p>
          </div>
        </div>
        <template v-else>
          <div class="chat-messages-section">
            <div class="chat-messages" ref="chatMessages" v-if="reversedMessages.length">
              <div class="message" :class="{ 'message-mine': msg.isMine }"
                   v-for="(msg, index) in reversedMessages" :key="index">
                <div class="message-avatar" @click="navigateToUser(msg.user && msg.user.id)">
                  {{ msg.initials }}
                </div>
                <div class="message-content">
                  <div class="message-header">
                          <span class="message-author"
                                @click="navigateToUser(msg.user && msg.user.id)">{{
                              msg.user && msg.user.username || 'Unknown'
                            }}</span>
                    <span class="message-time">{{ new Date(msg.created_at).toLocaleString('ru-RU') }}</span>
                  </div>
                  <div class="message-text">
                    {{ msg.message }}
                  </div>

                  <!-- Attachments -->
                  <div v-if="msg.attachments && msg.attachments.length > 0" class="message-attachments">
                    <div v-for="(attachment, attIndex) in msg.attachments" :key="attIndex" class="attachment-item">
                      <!-- Image Preview -->
                      <div v-if="isImage(attachment.name)" class="attachment-image-wrapper">
                        <img
                            :src="attachment.url"
                            :alt="attachment.name"
                            class="attachment-image"
                            width="200"
                            height="200"
                            @click="downloadFile(attachment.url, attachment.name)"
                        />
                        <div class="attachment-image-info">
                          <span class="attachment-name">{{ attachment.name }}</span>
                          <span class="attachment-size">{{ formatFileSize(attachment.size) }}</span>
                        </div>
                        <b-button
                            size="sm"
                            variant="light"
                            class="attachment-download-btn"
                            @click="downloadFile(attachment.url, attachment.name)"
                        >
                          <BIcon icon="download"/>
                        </b-button>
                      </div>

                      <!-- File -->
                      <div v-else class="attachment-file" @click="downloadFile(attachment.url, attachment.name)">
                        <div class="attachment-icon">
                          <BIcon :icon="getFileIcon(attachment.name)" font-scale="1.5"/>
                        </div>
                        <div class="attachment-info">
                          <div class="attachment-name">{{ attachment.name }}</div>
                          <div class="attachment-size">{{ formatFileSize(attachment.size) }}</div>
                        </div>
                        <div class="attachment-download">
                          <BIcon icon="download"/>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="chat-messages" ref="chatMessages">
              <p class="text-center text-muted">Сообщений пока нет</p>
            </div>
          </div>
          <div class="chat-input-wrapper">
            <div v-if="attachedFiles.length" class="attached-files-preview">
              <div v-for="(file, index) in attachedFiles" :key="index" class="attached-file-item">
                <img v-if="isImage(file.name)" :src="file.preview" class="attached-file-thumb" alt="preview"/>
                <i v-else :class="getFileIcon(file.name)" class="attached-file-icon"></i>
                <span class="attached-file-name" :title="file.name">{{ file.name }}</span>
                <span class="attached-file-size">{{ formatFileSize(file.size) }}</span>
                <b-button variant="link" size="sm" class="attached-file-remove" @click="removeAttachedFile(index)">
                  <BIcon icon="x"/>
                </b-button>
              </div>
            </div>
            <div class="chat-input">
              <b-form-textarea
                  v-model="newMessageText"
                  placeholder="Введите сообщение..."
                  rows="1"
                  class="mb-2 w-100"
                  @keydown.ctrl.enter="handleSendMessage"
              />
              <input
                  type="file"
                  ref="fileInput"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                  style="display: none"
                  @change="handleFileSelect"
              />
              <b-button variant="outline-secondary" size="sm" class="d-flex justify-content-center align-items-center attach-btn"
                        @click="$refs.fileInput.click()">
                <BIcon icon="paperclip"/>
              </b-button>
              <b-button variant="primary" size="sm" class="d-flex justify-content-center align-items-center" block
                        @click="handleSendMessage" :disabled="(!newMessageText.trim() && !attachedFiles.length) || isSendingMessage">
                <BIcon icon="cursor-fill"/>
              </b-button>
            </div>
          </div>
        </template>
      </div>
    </template>
    <div v-if="isLoading" class="d-flex justify-content-center align-items-center">
      <b-spinner variant="primary"></b-spinner>
    </div>
    <ejs-kanban v-else id="kanban"
                ref="kanbanObj"
                width="100%"
                height="100%"
                keyField="Status"
                cssClass="kanban-card-template"
                :dataSource="kanbanData"
                :cardSettings="cardSettings"
                :allowDragAndDrop="false"
                :cardDoubleClick="cancelKanbanCardDoubleClick"
                :dialogOpen="cancelKanbanDialog"
    >
      <e-columns>
        <e-column v-for="status in sortedStatuses"
                  :key="`status-col-id-${status.id}`"
                  :headerText="getStatusHeaderText(status)"
                  :keyField="status.slug"
                  :minWidth="300"
                  :maxWidth="320">
        </e-column>
      </e-columns>
    </ejs-kanban>

  </TicketLayouts>
</template>

<style scoped>
/* Основные стили канбана */
#kanban {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
  height: calc(100vh - 100px);
}

/* Фиксированная высота для контента колонок */
:deep(.e-kanban-table) {
  height: 100%;
}

:deep(.e-kanban-content) {
  max-height: calc(100vh - 180px);
  overflow-y: auto;
}

:deep(.e-card-wrapper) {
  max-height: calc(100vh - 180px);
  overflow-y: auto;
  overflow-x: hidden;
}

.kanban-card-template .e-kanban-column-header {
  background: #f9fafb;
  border-radius: 6px 6px 0 0;
  padding: 12px;
  font-weight: 600;
  color: #111827;
  font-size: 13px;
}

.kanban-card-template .e-card {
  margin: 6px;
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
  width: 100% !important;
  max-width: 100% !important;
}

:deep(td) {
  width: 267px !important;
  max-width: 267px !important;
}

:deep(th) {
  width: 267px !important;
  max-width: 267px !important;
}

.kanban-card-template .e-card .e-card-wrapper {
  padding: 0 !important;
  width: 100% !important;
}

.kanban-card-template .e-card table {
  table-layout: auto !important;
  width: 100% !important;
}

.kanban-card-template .e-card table td {
  background: none !important;
  border: none !important;
  padding: 0 !important;
}

/* Стили для drag and drop */
.kanban-card-template .e-card.e-dragging {
  opacity: 0.7;
  transform: rotate(2deg);
}

.kanban-card-template .e-card.e-cloned {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15) !important;
}

/* Стили колонок с учетом статусов */
.kanban-card-template .e-column[data-key="new"] .e-kanban-column-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.kanban-card-template .e-column[data-key="in-progress"] .e-kanban-column-header {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
}

.kanban-card-template .e-column[data-key="review"] .e-kanban-column-header {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
}

.kanban-card-template .e-column[data-key="done"] .e-kanban-column-header {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
  color: white;
}

.kanban-card-template .e-column[data-key="closed"] .e-kanban-column-header {
  background: linear-gradient(135deg, #a8a8a8 0%, #6c6c6c 100%);
  color: white;
}

/* Анимация добавления карточки */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.kanban-card-template .e-card {
  animation: slideIn 0.3s ease;
}

/* Счетчик карточек в колонке */
.kanban-card-template .e-item-count {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  margin-left: 6px;
}

/* Responsive для разных экранов */
@media (max-width: 1400px) {
  .kanban-card-template .e-kanban-column {
    min-width: 300px !important;
    max-width: 320px !important;
  }
}

@media (max-width: 1024px) {
  .kanban-card-template .e-kanban-column {
    min-width: 280px !important;
    max-width: 300px !important;
  }
}

@media (max-width: 768px) {
  .kanban-card-template .e-kanban-column {
    min-width: 260px !important;
    max-width: 280px !important;
  }

  .kanban-card-template .e-card {
    margin: 4px;
  }

  .kanban-card-template .e-kanban-column-header {
    padding: 10px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .kanban-card-template .e-kanban-column {
    min-width: 240px !important;
    max-width: 100% !important;
  }
}

/* Скроллбар для колонок */
.kanban-card-template .e-kanban-content::-webkit-scrollbar {
  width: 6px;
}

.kanban-card-template .e-kanban-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.kanban-card-template .e-kanban-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.kanban-card-template .e-kanban-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Dark mode */
@media (prefers-color-scheme: dark) {
  .kanban-card-template .e-kanban-column-header {
    background: #374151;
    color: #f9fafb;
  }

  .kanban-card-template .e-kanban-content::-webkit-scrollbar-track {
    background: #1f2937;
  }

  .kanban-card-template .e-kanban-content::-webkit-scrollbar-thumb {
    background: #4b5563;
  }

  .kanban-card-template .e-kanban-content::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }
}

/* Пустая колонка */
.kanban-card-template .e-empty-card {
  text-align: center;
  padding: 30px 15px;
  color: #9ca3af;
  font-size: 12px;
}

/* Hover эффект для колонки при перетаскивании */
.kanban-card-template .e-kanban-column.e-drag-hover {
  background-color: rgba(103, 126, 234, 0.05);
}

/* Swimlane styles */
.kanban-card-template .e-swimlane-header {
  background: #f3f4f6;
  border-radius: 6px;
  padding: 10px 12px;
  margin: 6px;
  font-weight: 600;
  color: #374151;
  font-size: 12px;
}

/* Sidebar styles */
.sidebar-header {
  padding-bottom: 15px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 20px;
}

.sidebar-header h5 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.sidebar-header p {
  margin: 5px 0 0 0;
  font-size: 13px;
}

.ticket-id {
  font-size: 0.75rem;
  line-height: 1rem;
  color: rgb(156 163 175);
  background: rgb(229 231 235);
  padding: 4px 8px;
  border-radius: 0.25rem;
}

.tabs button {
  background: transparent !important;
  border: none !important;
  color: black;
}

.tabs button.active {
  border-bottom: 2px solid #667eea !important;
  color: #667eea !important;
}

.history-list {
  overflow-y: auto;
  padding-right: 8px;
}

.history-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 10px;
  background: #f9fafb;
  transition: background 0.2s;
}

.history-item:hover {
  background: #f3f4f6;
}

.history-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #667eea;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.history-content {
  flex: 1;
}

.history-action {
  font-size: 14px;
  font-weight: 400;
  color: #111827;
  margin-bottom: 4px;
}

.history-action span {
  font-weight: 600;
}

.history-meta {
  display: flex;
  gap: 8px;
  font-size: 12px;
  color: #6b7280;
}

.history-user {
  font-weight: 500;
}

.history-date {
  color: #9ca3af;
}

/* Sidebar content wrapper — flex column for full height */
.sidebar-content-wrapper {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px);
  overflow: hidden;
}

.sidebar-top-section {
  flex-shrink: 0;
}

.sidebar-scroll-section {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.chat-messages-section {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 10px;
  margin-bottom: 0;
  min-height: 0;
  padding-right: 8px;
}

.message {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}

/* Свои сообщения справа */
.message-mine {
  flex-direction: row-reverse;
}

.message-mine .message-avatar {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.message-mine .message-header {
  flex-direction: row-reverse;
}

.message-mine .message-text {
  color: black;
}

.message-avatar {
  cursor: pointer;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.message-content {
  flex: 1;
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
}

.message-author {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  cursor: pointer;
}

.message-time {
  font-size: 11px;
  color: #9ca3af;
}

.message-text {
  font-size: 13px;
  color: #374151;
  background: #f9fafb;
  padding: 10px 12px;
  border-radius: 8px;
  line-height: 1.5;
}

/* Attachments Styles */
.message-attachments {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.attachment-item {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

/* Image Attachment */
.attachment-image-wrapper {
  position: relative;
  cursor: pointer;
}

.attachment-image {
  width: 100%;
  max-width: 100%;
  height: auto;
  max-height: 300px;
  object-fit: cover;
  display: block;
  transition: opacity 0.2s;
}

.attachment-image:hover {
  opacity: 0.9;
}

.attachment-image-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f9fafb;
  font-size: 12px;
}

.attachment-download-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.9) !important;
  border: none !important;
  padding: 6px 10px;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  opacity: 0;
  transition: opacity 0.2s;
}

.attachment-image-wrapper:hover .attachment-download-btn {
  opacity: 1;
}

/* File Attachment */
.attachment-file {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  cursor: pointer;
  transition: background 0.2s;
}

.attachment-file:hover {
  background: #f9fafb;
}

.attachment-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  flex-shrink: 0;
}

.attachment-info {
  flex: 1;
  min-width: 0;
}

.attachment-name {
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-size {
  font-size: 11px;
  color: #9ca3af;
  display: block;
  margin-top: 2px;
}

.attachment-download {
  color: #667eea;
  flex-shrink: 0;
}

.message-attachments.has-multiple-images {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px;
}

.message-attachments.has-multiple-images .attachment-image {
  max-height: 150px;
}

.chat-input-wrapper {
  flex-shrink: 0;
  padding: 10px 0 0 0;
}

.chat-input {
  background: transparent !important;
  border-top: 0 solid transparent;
  padding: 5px 0 0 0;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  height: auto;
  min-height: 63px;
  flex-shrink: 0;
}

.attach-btn {
  width: 40px !important;
  height: 50px;
  flex-shrink: 0;
  border-color: #ced4da;
  color: #6c757d;
}

.attach-btn:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
  color: #495057;
}

.attached-files-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 0;
}

.attached-file-item {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f0f2f5;
  border-radius: 8px;
  padding: 4px 8px;
  max-width: 100%;
}

.attached-file-thumb {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 4px;
}

.attached-file-icon {
  font-size: 18px;
  color: #6c757d;
}

.attached-file-name {
  font-size: 12px;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attached-file-size {
  font-size: 11px;
  color: #999;
}

.attached-file-remove {
  padding: 0 2px;
  color: #999;
  font-size: 14px;
}

.attached-file-remove:hover {
  color: #dc3545;
}

.chat-input textarea {
  height: 50px;
  padding: 12px 15px !important;
  font-size: 14px;
  resize: none;
  flex: 1;
}

.chat-input button {
  width: 40px !important;
  height: 50px;
  flex-shrink: 0;
}

@media screen and (max-width: 630px) {
  .chat-input {
    padding: 12px 0 0 0;
    min-height: 55px;
  }

  .chat-input textarea {
    min-height: 40px;
    height: auto;
    max-height: 120px;
    padding: 10px 12px !important;
  }

  .chat-input button {
    height: 40px;
    min-height: 40px;
  }
}

.history-list::-webkit-scrollbar,
.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.history-list::-webkit-scrollbar-track,
.chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.history-list::-webkit-scrollbar-thumb,
.chat-messages::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.history-list::-webkit-scrollbar-thumb:hover,
.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Responsive для истории */
@media (max-height: 800px) {
  .history-list {
    max-height: calc(100vh - 280px);
  }
}

@media (max-height: 600px) {
  .history-list {
    max-height: calc(100vh - 240px);
    min-height: 200px;
  }
}

@media (max-width: 768px) {
  .history-list {
    max-height: calc(100vh - 320px);
  }

  .history-item {
    padding: 10px;
    margin-bottom: 8px;
  }

  .history-icon {
    width: 28px;
    height: 28px;
  }

  .history-action {
    font-size: 13px;
  }

  .history-meta {
    font-size: 11px;
    flex-direction: column;
    gap: 4px;
  }
}

/* Индикатор загрузки для колонок */
:deep(.column-loading-indicator) {
  display: none;
  align-items: center;
  justify-content: center;
  padding: 15px;
  margin: 10px;
  background: #f9fafb;
  border-radius: 8px;
  font-size: 13px;
  color: #667eea;
  gap: 10px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

:deep(.column-loading-indicator .spinner-border) {
  width: 1.2rem;
  height: 1.2rem;
  border-width: 2px;
}

</style>